(function () {
  const settings = window.MinimogSettings;
  const campaign = settings && settings.final_price;
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const cartUpdateUrl = `${root}cart/update.js`;

  const REFERRAL_PARAMETER = 'ref';
  const REFERRAL_COOKIE = 'nelpanda_ref';
  const REFERRAL_CODE_COOKIE = 'nelpanda_ref_code';
  const REFERRAL_TOUCH_COOKIE = 'nelpanda_ref_touch';
  const REFERRAL_BROWSER_COOKIE = 'nelpanda_ref_browser';
  const REFERRAL_COOKIE_TTL_SECONDS = 30 * 24 * 60 * 60;
  const REFERRAL_REFERENCE_PATTERN = /^[a-z0-9_-]{3,128}$/;
  const REFERRAL_DISCOUNT_CODE_PATTERN = /^[A-Z0-9_-]{4,64}$/i;
  const REFERRAL_TOUCH_TOKEN_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
  const REFERRAL_BROWSER_KEY_PATTERN = /^[A-Za-z0-9_-]{22,128}$/;
  const MAX_ATTRIBUTION_WINDOW_DAYS = 365;
  // Cold resolver connections can exceed 3s; fail open after a buyer-tolerable 5s.
  const RESOLVER_TIMEOUT_MS = 5000;

  function normalizeDiscountCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  function normalizeReference(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isValidReference(reference) {
    return REFERRAL_REFERENCE_PATTERN.test(reference);
  }

  function isValidDiscountCode(code) {
    return typeof code === 'string' && REFERRAL_DISCOUNT_CODE_PATTERN.test(code);
  }

  function isValidTouchToken(token) {
    return typeof token === 'string'
      && token.length <= 1024
      && REFERRAL_TOUCH_TOKEN_PATTERN.test(token);
  }

  function isValidBrowserKey(browserKey) {
    return typeof browserKey === 'string' && REFERRAL_BROWSER_KEY_PATTERN.test(browserKey);
  }

  function isValidAttributionWindowDays(days) {
    return Number.isInteger(days) && days >= 1 && days <= MAX_ATTRIBUTION_WINDOW_DAYS;
  }

  function readCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const entry = String(document.cookie || '')
      .split(';')
      .map((value) => value.trim())
      .find((value) => value.startsWith(prefix));

    if (!entry) return null;

    try {
      return decodeURIComponent(entry.slice(prefix.length));
    } catch (_error) {
      return entry.slice(prefix.length);
    }
  }

  function cookieSecurityAttribute() {
    return window.location?.protocol === 'https:' ? '; Secure' : '';
  }

  function writeCookie(name, value, maxAge = REFERRAL_COOKIE_TTL_SECONDS) {
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${cookieSecurityAttribute()}`;
  }

  function clearCookie(name) {
    document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax${cookieSecurityAttribute()}`;
  }

  function clearReferralCookies() {
    [REFERRAL_COOKIE, REFERRAL_CODE_COOKIE, REFERRAL_TOUCH_COOKIE]
      .forEach(clearCookie);
  }

  function referralCookieState() {
    const rawReference = readCookie(REFERRAL_COOKIE);
    const rawCode = readCookie(REFERRAL_CODE_COOKIE);
    const rawTouchToken = readCookie(REFERRAL_TOUCH_COOKIE);
    const rawBrowserKey = readCookie(REFERRAL_BROWSER_COOKIE);
    const reference = rawReference === null ? '' : normalizeReference(rawReference);
    const discountCode = rawCode === null ? '' : normalizeDiscountCode(rawCode);

    if (rawReference !== null && !isValidReference(reference)) {
      clearReferralCookies();
      if (rawBrowserKey !== null && !isValidBrowserKey(rawBrowserKey)) {
        clearCookie(REFERRAL_BROWSER_COOKIE);
      }
      return {
        reference: '',
        discountCode: '',
        touchToken: '',
        browserKey: '',
        invalid: true,
      };
    }

    const touchToken = isValidTouchToken(rawTouchToken) ? rawTouchToken : '';
    const browserKey = isValidBrowserKey(rawBrowserKey) ? rawBrowserKey : '';
    if (rawCode !== null && (!isValidDiscountCode(discountCode) || !reference)) {
      clearCookie(REFERRAL_CODE_COOKIE);
    }
    if (rawTouchToken !== null && (!touchToken || !reference)) {
      clearCookie(REFERRAL_TOUCH_COOKIE);
    }
    if (rawBrowserKey !== null && !browserKey) {
      clearCookie(REFERRAL_BROWSER_COOKIE);
    }

    return {
      reference,
      discountCode: isValidDiscountCode(discountCode) ? discountCode : '',
      touchToken,
      browserKey,
      invalid: false,
    };
  }

  function randomBrowserKey() {
    const cryptoObject = window.crypto || globalThis.crypto;
    if (typeof cryptoObject?.getRandomValues !== 'function') return '';

    try {
      const bytes = new Uint8Array(16);
      cryptoObject.getRandomValues(bytes);
      return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch (_error) {
      return '';
    }
  }

  function readReferralSource() {
    const stored = referralCookieState();
    let hasQueryReference = false;
    let queryReference = '';

    try {
      const params = new URLSearchParams(window.location?.search || '');
      hasQueryReference = params.has(REFERRAL_PARAMETER);
      queryReference = params.get(REFERRAL_PARAMETER) || '';
    } catch (_error) {
      hasQueryReference = false;
      queryReference = '';
    }

    if (hasQueryReference) {
      const reference = normalizeReference(queryReference);
      if (!isValidReference(reference)) {
        clearReferralCookies();
        return { reference: '', source: 'query', invalid: true };
      }

      return {
        reference,
        source: 'query',
        invalid: false,
        browserKey: stored.browserKey || randomBrowserKey(),
        touchToken: '',
      };
    }

    if (!stored.reference) {
      if (stored.discountCode || stored.touchToken) clearReferralCookies();
      return { reference: '', source: 'none', invalid: stored.invalid };
    }

    return {
      reference: stored.reference,
      source: 'cookie',
      invalid: false,
      browserKey: stored.browserKey,
      touchToken: stored.touchToken,
      discountCode: stored.discountCode,
    };
  }

  function normalizeOrderDiscount(discount) {
    const percentage = Math.round(Number(discount && (discount.percentage ?? discount.percent) || 0));
    const rate = Number(discount && discount.rate || 100 - percentage);
    const label = String(discount && discount.label || '').trim();
    const active = Boolean(
      discount
      && discount.active
      && label
      && percentage > 0
      && percentage < 100
      && rate > 0
      && rate < 100,
    );

    return {
      active,
      label: active ? label : '',
      percentage: active ? percentage : 0,
      rate: active ? rate : 100,
      ...(active && discount.allItems === true ? { allItems: true } : {}),
    };
  }

  function createOrderDiscount(label, percentage, allItems) {
    return {
      active: true,
      label,
      percentage,
      rate: 100 - percentage,
      ...(allItems ? { allItems: true } : {}),
    };
  }

  function configuredOrderDiscountPercentage() {
    const percentage = Number(settings?.order_discount?.percentage);
    return Number.isInteger(percentage) && percentage >= 1 && percentage <= 99 ? percentage : 0;
  }

  function validateResolverOffer(payload, reference, source) {
    if (!payload || typeof payload !== 'object' || payload.ok !== true) return null;

    const resolverCampaign = payload.campaign;
    if (!resolverCampaign || typeof resolverCampaign !== 'object') return null;

    const rawDiscountCode = resolverCampaign.discountCode;
    const label = typeof resolverCampaign.label === 'string' ? resolverCampaign.label.trim() : '';
    const percentage = resolverCampaign.percentage;
    if (
      typeof rawDiscountCode !== 'string'
      || !/^[A-Z0-9_-]{4,64}$/i.test(rawDiscountCode)
      || !label
      || label.length > 64
      || typeof percentage !== 'number'
      || !Number.isInteger(percentage)
      || percentage < 1
      || percentage > 99
    ) return null;

    const discountCode = normalizeDiscountCode(rawDiscountCode);
    const attributionWindowDays = resolverCampaign.attributionWindowDays;
    const touchFieldNames = ['attributionWindowDays', 'touchToken', 'expiresAt'];
    const legacyStatic = source === 'query'
      && !touchFieldNames.some((field) => Object.prototype.hasOwnProperty.call(resolverCampaign, field));
    if (attributionWindowDays !== undefined && !isValidAttributionWindowDays(attributionWindowDays)) return null;

    if (source === 'query' && !legacyStatic) {
      if (
        !isValidAttributionWindowDays(attributionWindowDays)
        || !isValidTouchToken(resolverCampaign.touchToken)
        || typeof resolverCampaign.expiresAt !== 'string'
        || resolverCampaign.expiresAt.length > 64
        || !Number.isFinite(Date.parse(resolverCampaign.expiresAt))
        || Date.parse(resolverCampaign.expiresAt) <= Date.now()
      ) return null;
    }

    return {
      reference,
      discountCode,
      // Dynamic Program offers use the Core's all-item discount contract; static campaigns retain product eligibility.
      orderDiscount: createOrderDiscount(label, percentage, isValidAttributionWindowDays(attributionWindowDays)),
      attributionWindowDays: isValidAttributionWindowDays(attributionWindowDays)
        ? attributionWindowDays
        : null,
      touchToken: source === 'query' ? resolverCampaign.touchToken : '',
      expiresAt: source === 'query' ? resolverCampaign.expiresAt : '',
      legacyStatic,
    };
  }

  function fetchRequest(url, options) {
    if (typeof window.fetch === 'function') return window.fetch(url, options);
    if (typeof fetch === 'function') return fetch(url, options);
    return Promise.reject(new Error('Fetch is unavailable'));
  }

  function abortControllerConstructor() {
    if (typeof window.AbortController === 'function') return window.AbortController;
    if (typeof globalThis.AbortController === 'function') return globalThis.AbortController;
    return null;
  }

  function timeoutFunctions() {
    const schedule = typeof window.setTimeout === 'function'
      ? window.setTimeout.bind(window)
      : typeof globalThis.setTimeout === 'function' ? globalThis.setTimeout : null;
    const cancel = typeof window.clearTimeout === 'function'
      ? window.clearTimeout.bind(window)
      : typeof globalThis.clearTimeout === 'function' ? globalThis.clearTimeout : null;
    return { schedule, cancel };
  }

  async function requestReferral(referralSource, resolverUrl, body = null) {
    const { reference, source, browserKey } = referralSource;
    if (source === 'query' && !isValidBrowserKey(browserKey)) {
      return { status: 'transient', reason: 'browser_key_unavailable' };
    }

    const AbortControllerClass = abortControllerConstructor();
    const { schedule, cancel } = timeoutFunctions();
    let controller = null;
    let timeoutId = null;

    if (AbortControllerClass) {
      try {
        controller = new AbortControllerClass();
      } catch (_error) {
        controller = null;
      }
    }

    if (controller && schedule) {
      timeoutId = schedule(() => controller.abort(), RESOLVER_TIMEOUT_MS);
    }

    const options = {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body || (source === 'query'
        ? { reference, source, browserKey }
        : { reference, source })),
    };
    if (controller) options.signal = controller.signal;

    try {
      const response = await fetchRequest(resolverUrl, options);
      if (response?.status === 404) return { status: 'not_found' };
      if (!response?.ok || (response.status !== undefined && response.status !== 200)) {
        return { status: 'transient' };
      }

      let payload;
      try {
        payload = await response.json();
      } catch (_error) {
        return { status: 'invalid' };
      }

      const offer = validateResolverOffer(payload, reference, source);
      return offer ? { status: 'success', offer } : { status: 'invalid' };
    } catch (error) {
      return { status: 'transient', error };
    } finally {
      if (timeoutId !== null && cancel) cancel(timeoutId);
    }
  }

  const defaultDiscountCode = normalizeDiscountCode(campaign && campaign.discount_code);
  const defaultDiscountEnabled = Boolean(campaign?.active && defaultDiscountCode);
  const baselineOrderDiscount = normalizeOrderDiscount(settings?.order_discount);
  const baselineOrderDiscountPercentage = configuredOrderDiscountPercentage();
  const referralSource = readReferralSource();
  const resolverUrl = String(settings?.referral_resolver_url || '').trim();

  if (!settings) {
    delete document.documentElement.dataset.referralOffer;
    return;
  }

  let referral = null;
  let referralAttribution = null;
  let cartAttributeMode = referralSource.invalid || !referralSource.reference ? 'clear' : 'preserve';
  let cartCodeOffer = null;
  let resolvedCartCodes = null;
  const state = {
    status: 'loading',
    applicable: false,
    referral: {
      active: false,
      reference: referralSource.reference,
      discountCode: '',
      orderDiscount: null,
      resolvedOffer: null,
      applicable: false,
    },
  };

  function sameOrderDiscount(left, right) {
    return Boolean(
      left
      && right
      && Boolean(left.active) === Boolean(right.active)
      && String(left.label || '') === String(right.label || '')
      && Number(left.percentage || 0) === Number(right.percentage || 0)
      && Number(left.rate || 100) === Number(right.rate || 100)
      && (left.allItems === true) === (right.allItems === true),
    );
  }

  function setProjectedOrderDiscount(orderDiscount, code = '') {
    const projected = { ...orderDiscount };
    settings.order_discount = projected;
    try {
      window.NelpandaFinalPrice?.setOrderDiscountCode?.(code, projected.label);
      window.NelpandaFinalPrice?.setOrderDiscount?.(projected);
    } catch (error) {
      console.error(error);
    }
  }

  function writeResolvedReferralCookies(offer, browserKey) {
    if (offer.legacyStatic) {
      writeCookie(REFERRAL_COOKIE, offer.reference);
      return;
    }

    const maxAge = offer.attributionWindowDays * 24 * 60 * 60;
    writeCookie(REFERRAL_COOKIE, offer.reference, maxAge);
    writeCookie(REFERRAL_CODE_COOKIE, offer.discountCode, maxAge);
    writeCookie(REFERRAL_TOUCH_COOKIE, offer.touchToken, maxAge);
    writeCookie(REFERRAL_BROWSER_COOKIE, browserKey, maxAge);
  }

  function applyResolverResult(result) {
    if (result.status === 'success') {
      const offer = result.offer;
      state.referral.reference = offer.reference;
      state.referral.resolvedOffer = {
        discountCode: offer.discountCode,
        label: offer.orderDiscount.label,
        percentage: offer.orderDiscount.percentage,
      };
      if (offer.legacyStatic) {
        referralAttribution = null;
        cartAttributeMode = 'clear';
      } else {
        referralAttribution = {
          code: offer.discountCode,
          touchToken: referralSource.source === 'query' ? offer.touchToken : referralSource.touchToken,
        };
        cartAttributeMode = 'set';
      }
      if (referralSource.source === 'query') {
        writeResolvedReferralCookies(offer, referralSource.browserKey);
      }

      if (offer.orderDiscount.percentage > baselineOrderDiscountPercentage) {
        referral = offer;
        state.referral.active = true;
        state.referral.discountCode = offer.discountCode;
        state.referral.orderDiscount = { ...offer.orderDiscount };
        setProjectedOrderDiscount(offer.orderDiscount, offer.discountCode);
      } else {
        referral = null;
        state.referral.active = false;
        state.referral.discountCode = '';
        state.referral.orderDiscount = null;
        setProjectedOrderDiscount(baselineOrderDiscount);
      }
      return;
    }

    referral = null;
    referralAttribution = null;
    state.referral.active = false;
    state.referral.discountCode = '';
    state.referral.orderDiscount = null;
    state.referral.resolvedOffer = null;
    if (result.status === 'not_found' || result.status === 'invalid') {
      clearReferralCookies();
      state.referral.reference = '';
      cartAttributeMode = 'clear';
    } else if (referralSource.reference) {
      // A resolver outage must not erase a valid prior attribution from the cart.
      cartAttributeMode = 'preserve';
    }
    setProjectedOrderDiscount(baselineOrderDiscount);
  }

  const resolverInitialization = (referralSource.reference && resolverUrl
    ? requestReferral(referralSource, resolverUrl)
    : Promise.resolve({ status: 'unavailable' })
  ).then((result) => {
    applyResolverResult(result);
    return result;
  }).catch((error) => {
    applyResolverResult({ status: 'transient', error });
    return { status: 'transient', error };
  }).finally(() => {
    delete document.documentElement.dataset.referralOffer;
  });

  document.documentElement.dataset.defaultDiscount = state.status;

  function handleError(error) {
    state.status = 'error';
    document.documentElement.dataset.defaultDiscount = state.status;
    console.error(error);
    return null;
  }

  function cartDiscountCodes(cart) {
    return (cart.discount_codes || [])
      .map((entry) => normalizeDiscountCode(entry.code))
      .filter(Boolean);
  }

  function uniqueDiscountCodes(codes) {
    return [...new Set(codes.map(normalizeDiscountCode).filter(Boolean))];
  }

  function sameDiscountCodes(left, right) {
    const leftSet = new Set(left);
    const rightSet = new Set(right);
    return leftSet.size === rightSet.size && [...leftSet].every((code) => rightSet.has(code));
  }

  function containsDiscountCodes(cart, requestedCodes) {
    const returnedCodes = new Set(cartDiscountCodes(cart));
    return requestedCodes.every((code) => returnedCodes.has(code));
  }

  function cartHasMerchandise(cart) {
    return (Array.isArray(cart?.items) && cart.items.length > 0) || Number(cart.item_count) > 0;
  }

  function cartEligibilityFingerprint(cart) {
    if (Array.isArray(cart?.items) && cart.items.length > 0) {
      const items = cart.items
        .map((item) => ({
          key: String(item?.key ?? ''),
          id: String(item?.id ?? ''),
          variant_id: String(item?.variant_id ?? ''),
          quantity: Number.isFinite(Number(item?.quantity))
            ? Number(item.quantity)
            : String(item?.quantity ?? ''),
        }))
        .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));

      return `items:${JSON.stringify(items)}`;
    }

    const itemCount = Number(cart?.item_count);
    return `item_count:${Number.isFinite(itemCount) ? itemCount : String(cart?.item_count ?? '')}`;
  }

  async function resolveCartCodeOffer(cart) {
    const codes = uniqueDiscountCodes(cartDiscountCodes(cart))
      .filter((code) => code !== defaultDiscountCode)
      .slice(0, 5);
    const fingerprint = [...codes].sort().join(',');
    if (resolvedCartCodes === fingerprint) return;

    resolvedCartCodes = fingerprint;
    cartCodeOffer = null;
    if (!resolverUrl || referral) return;

    for (const code of codes) {
      const reference = normalizeReference(code);
      if (!isValidReference(reference)) continue;

      const result = await requestReferral(
        { reference, source: 'discount_code', browserKey: '' },
        resolverUrl,
        { discountCode: code },
      );
      if (result.status === 'transient') {
        resolvedCartCodes = null;
        return;
      }
      if (
        result.status === 'success'
        && result.offer.discountCode === code
        && result.offer.orderDiscount.percentage > baselineOrderDiscountPercentage
      ) {
        cartCodeOffer = result.offer;
        return;
      }
    }
  }

  function desiredDiscountCodes(cart) {
    const desiredCodes = [...cartDiscountCodes(cart)];

    if (defaultDiscountEnabled) desiredCodes.push(defaultDiscountCode);
    if (referral) desiredCodes.push(referral.discountCode);

    return uniqueDiscountCodes(desiredCodes);
  }

  function cartReferralAttributePatch(cart) {
    if (cartAttributeMode === 'preserve') return null;

    const desired = cartAttributeMode === 'set'
      ? {
          _referral_touch: referralAttribution?.touchToken || '',
          _referral_code: referralAttribution?.code || '',
        }
      : { _referral_touch: '', _referral_code: '' };
    const current = cart?.attributes && typeof cart.attributes === 'object'
      ? cart.attributes
      : {};
    const patch = {};

    Object.entries(desired).forEach(([key, value]) => {
      if (String(current[key] ?? '') !== value) patch[key] = value;
    });

    return Object.keys(patch).length ? patch : null;
  }

  function updateOrderDiscountFromCart(cart) {
    const offer = referral || cartCodeOffer;
    const projected = offer ? offer.orderDiscount : baselineOrderDiscount;
    if (!cartHasMerchandise(cart)) {
      if (!sameOrderDiscount(settings.order_discount, projected)) {
        setProjectedOrderDiscount(projected, offer?.discountCode);
      }
      return;
    }

    const finalPrice = window.NelpandaFinalPrice;
    if (typeof finalPrice?.setCart !== 'function') {
      setProjectedOrderDiscount(state.referral.applicable && referral ? referral.orderDiscount : baselineOrderDiscount);
      return;
    }

    settings.order_discount = { ...baselineOrderDiscount };
    try {
      finalPrice.setOrderDiscountCode?.(
        state.referral.applicable && referral ? referral.discountCode : '',
        state.referral.applicable && referral ? referral.orderDiscount.label : '',
      );
      finalPrice.setCart(cart);
    } catch (error) {
      console.error(error);
      setProjectedOrderDiscount(state.referral.applicable && referral ? referral.orderDiscount : baselineOrderDiscount);
    }
  }

  function updateState(cart) {
    const applicableCodes = new Set(
      (cart.discount_codes || [])
        .filter((candidate) => candidate.applicable)
        .map((candidate) => normalizeDiscountCode(candidate.code)),
    );

    state.status = 'loaded';
    state.applicable = defaultDiscountEnabled && applicableCodes.has(defaultDiscountCode);
    state.referral.applicable = Boolean(referral && applicableCodes.has(referral.discountCode));
    document.documentElement.dataset.defaultDiscount = state.status;
    updateOrderDiscountFromCart(cart);
    document.dispatchEvent(new CustomEvent('nelpanda:default-discount', { detail: { ...state } }));
  }

  let omissionGuard = null;

  async function runApply({ refreshCart = false } = {}) {
    let response = await fetchRequest(cartUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

    let cart = await response.json();
    await resolveCartCodeOffer(cart);
    const currentCodes = uniqueDiscountCodes(cartDiscountCodes(cart));
    const desiredCodes = desiredDiscountCodes(cart);
    let changed = false;
    const fingerprint = cartEligibilityFingerprint(cart);
    const attributePatch = cartReferralAttributePatch(cart);

    if (omissionGuard && containsDiscountCodes(cart, desiredCodes)) omissionGuard = null;

    const shouldUpdateDiscount = (
      cartHasMerchandise(cart)
      && !sameDiscountCodes(currentCodes, desiredCodes)
      && omissionGuard?.fingerprint !== fingerprint
    );
    if (shouldUpdateDiscount || attributePatch) {
      const body = {};
      if (shouldUpdateDiscount) body.discount = desiredCodes.join(',');
      if (attributePatch) body.attributes = attributePatch;
      response = await fetchRequest(cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Discount request failed with status ${response.status}`);

      cart = await response.json();
      changed = true;
      if (shouldUpdateDiscount) {
        omissionGuard = containsDiscountCodes(cart, desiredCodes)
          ? null
          : { fingerprint: cartEligibilityFingerprint(cart) };
      }
    }

    updateState(cart);

    if ((state.applicable || state.referral.applicable) && changed && refreshCart) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', { detail: { preserveHeader: true } }),
      );
    }

    return cart;
  }

  let activeApply = null;
  let queuedApplyOptions = null;
  const applyWaiters = [];

  function mergeApplyOptions(left, right) {
    return {
      refreshCart: Boolean(left?.refreshCart || right?.refreshCart),
    };
  }

  async function drainApply(initialOptions) {
    let options = initialOptions;
    let cart;

    try {
      await resolverInitialization;
      while (options) {
        cart = await runApply(options);
        options = queuedApplyOptions;
        queuedApplyOptions = null;
      }

      const waiters = applyWaiters.splice(0);
      waiters.forEach(({ resolve }) => resolve(cart));
      return cart;
    } catch (error) {
      const waiters = applyWaiters.splice(0);
      queuedApplyOptions = null;
      waiters.forEach(({ reject }) => reject(error));
      throw error;
    } finally {
      activeApply = null;
    }
  }

  function apply(options = {}) {
    const promise = new Promise((resolve, reject) => {
      applyWaiters.push({ resolve, reject });
    });

    if (activeApply) {
      queuedApplyOptions = mergeApplyOptions(queuedApplyOptions, options);
      return promise;
    }

    activeApply = Promise.resolve().then(() => drainApply(options));
    activeApply.catch(() => {});
    return promise;
  }

  const api = {
    apply,
    ready: null,
    state,
    get pending() {
      return activeApply;
    },
    get referral() {
      return referral;
    },
    unsubscribe: null,
  };
  const initialize = () => apply({ refreshCart: true }).catch(handleError);

  if (document.readyState === 'loading' || document.readyState === 'interactive') {
    api.ready = new Promise((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(initialize()), { once: true });
    });
  } else {
    api.ready = initialize();
  }

  const cartUpdateEvent = window.MinimogTheme?.pubSubEvents?.cartUpdate;
  if (cartUpdateEvent && window.MinimogEvents?.subscribe) {
    api.unsubscribe = window.MinimogEvents.subscribe(cartUpdateEvent, () => {
      api.ready = apply({ refreshCart: true }).catch(handleError);
    });
  }

  window.NelpandaDefaultDiscount = api;
})();
